import { IS_MOBILE } from 'src/helpers/environment';
import { makeStyles } from 'tss-react/mui';

import { SCROLLBAR_BACKGROUND_COLOR, SCROLLBAR_BORDER } from './use-scrollbar-styles';

export const useScrollableStyles = makeStyles()(() => ({
  /**
   * The square of unused space at the bottom-right corner of the scrollable when both horizontal
   * and vertical static scrollbars are present. The only purpose of this element is to color this
   * square the same color as the scrollbar background.
   *
   * Note that although this element is rendered in the DOM unconditionally, it has a width and/or
   * height of zero in all cases except when both static scrollbars are visible, making it invisible
   * by default. This is due to the use of CSS grid as explained in the comments for
   * useScrollbarStyles.
   */
  bottomRightSquare: {
    /**
     * If the scrollbars have a border, fill in the 1px square in the top-left corner of this
     * element where the scrollbars' borders should intersect.
     */
    '&::after': {
      borderBottom: SCROLLBAR_BORDER,
      borderRight: SCROLLBAR_BORDER,
      content: "''",
      inset: 0,
      position: 'absolute',
    },
    /**
     * If the scrollbars have a border, also apply this border to this element. It's necessary to do
     * so using a pseudo-element to prevent the border from making this element's size non-zero when
     * it's supposed to be hidden.
     */
    '&::before': {
      borderLeft: SCROLLBAR_BORDER,
      borderTop: SCROLLBAR_BORDER,
      content: "''",
      left: 0,
      position: 'absolute',
      top: 0,
    },
    background: SCROLLBAR_BACKGROUND_COLOR,
    gridColumn: 2,
    gridRow: 2,
    /**
     * Ensure that the pseudo-elements' borders are hidden if this element has a width and/or height
     * of zero.
     */
    overflow: 'hidden',
    position: 'relative',
  },

  /** The container for the scrollable and its scrollbars. */
  root: {
    display: 'grid',
    /** Ensure that the top-left grid cell (the scrollable) fills the available space. */
    grid: '1fr / 1fr',
    position: 'relative',
  },

  /** The scrollable DOM element. */
  scrollable: {
    height: '100%',
    overflow: 'auto',
    /** TODO: The custom scrollbar is broken on mobile (ENG-5219). */
    scrollbarWidth: IS_MOBILE ? 'auto' : 'none',
    width: '100%',
  },

  /**
   * An element inside the scrollable that wraps all of its children. This is required solely to
   * observe changes to the total width and height of the scrollable's children so that the
   * scrollbars can be updated accordingly.
   */
  scrollableInner: {},
}));
